import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ROUTES, PAGGING_MAX_PAGES, FIRST_PAGE } from "~/const";
import { trans, getLink } from "~/providers/lang";
import ModalGallery from "~/components/ModalGallery";
import { getSmallPhoto, getDebugCaption, getFileName } from "~/utils/photo";
import { getItemInd } from "~/utils/utils";
import type { IPhotoItem } from "~/interfaces";
import { getDebug, setDebug } from "~/providers/debug";
import { mainStore } from "~/stores/main";

import "./style.less";

interface IGalleryList {
	baseUrl: string;
	title: string;
	desc?: string;
	desc2?: string;
	source: any;
}

export default function GalleryList({
	baseUrl = "",
	title = "",
	desc = "",
	desc2 = "",
	source = {},
}: IGalleryList) {
	const curLang = mainStore(state => state.data.lang);
	const { search, pathname } = useLocation();
	const navigate = useNavigate();
	const textareaEl = useRef<HTMLTextAreaElement>(null);
	const [state, setState] = useState<{
		photo: string;
		debug: boolean;
		selected: Array<string>;
	}>({
		photo: "",
		debug: getDebug(),
		selected: [],
	});

	function getPageUrl(pageNumber: number): string {
		return `${baseUrl}?strana=${pageNumber.toString()}`;
	}

	const searchParams = new URLSearchParams(search);
	const queryPage = searchParams.get("strana");
	const pageValue = queryPage && !isNaN(parseFloat(queryPage)) ? parseFloat(queryPage) : FIRST_PAGE;
	const page = pageValue >= FIRST_PAGE ? pageValue : FIRST_PAGE;
	const maxPage = source.length;
	const items: Array<IPhotoItem> = source[page - 1]
		? source[page - 1].map((item: IPhotoItem) => ({
			...item,
			desc: item.uni || item[curLang] || "",
		}))
		: [];
	const hasPrev = page !== FIRST_PAGE;
	const hasNext = page !== maxPage;
	const pages = [];

	for (let ind = page; ind <= maxPage; ind++) {
		pages.push({
			url: getPageUrl(ind),
			className: ["item", "hover", ...ind === page ? ["active"] : []].join(" "),
			ind,
		});

		if (pages.length === PAGGING_MAX_PAGES) {
			break;
		}
	}

	function openPhoto(item: IPhotoItem) {
		if (state.debug) {
			setState(prev => {
				const selected = prev.selected;
				const ind = selected.indexOf(item.photo);

				if (ind === -1) {
					selected.push(item.photo);
				} else {
					selected.splice(ind, 1);
				}

				return {
					...prev,
					selected,
				};
			});

			return;
		}

		setState(prev => ({
			...prev,
			photo: item.photo,
		}));
		navigate(`${pathname}?strana=${page}&fotka=${getFileName(item.photo)}`, {
			replace: true,
		});
	}

	function modalGalleryClose() {
		setState(prev => ({
			...prev,
			photo: "",
		}));
		navigate(`${pathname}?strana=${page}`, {
			replace: true,
		});
	}

	function toggleDebugMode() {
		setState(prev => {
			const newValue = !prev.debug;

			setDebug(newValue);

			return {
				...prev,
				debug: newValue,
			};
		});
	}

	function copyValues() {
		try {
			textareaEl.current?.select();
			navigator.clipboard.writeText(textareaEl.current?.value || "");
		} catch (exc) {
			/* eslint-disable no-console */
			console.log(exc);
		}
	}

	function getDebugInfo() {
		if (state.selected.length) {
			const selItems = state.selected.map(item => getFileName(item)).join(", ");

			return selItems;
		}

		return "";
	}

	function getItemClassName(item: IPhotoItem): string {
		return ["gallery-item", ...state.selected.indexOf(item.photo) === -1 ? [] : ["selected"]].join(" ");
	}

	useEffect(() => {
		const photo = searchParams.get("fotka");

		if (photo) {
			const ind = getItemInd(items, (item: IPhotoItem) => getFileName(item.photo) === photo);

			if (ind !== -1) {
				openPhoto(items[ind]);
			}
		}
	}, []);

	useEffect(() => {
		document.querySelector(".header")?.scrollIntoView();
	}, [page]);

	return <>
		<div className="body-background">
			<div className="top-shadow gallery-list page-content">
				<h1 className="gallery-title other-page-title" onDoubleClick={toggleDebugMode}>
					{title}
				</h1>
				<div className="row">
					{ state.debug && <div className="debug-container">
						<div className="space"></div>
						<div>
							<textarea defaultValue={getDebugInfo()} ref={textareaEl}></textarea>
						</div>
						<div>
							<button type="button" onClick={copyValues}>Kopírovat</button>
						</div>
					</div> }
					{ desc && <div className="gallery-desc">
						<p>
							{desc}
						</p>
						{ desc2 && <p>{desc2}</p>}
					</div>}
				</div>
				<div className="row gallery-list">
					{items.map((item, ind) => <div className="col-md-3" key={item.photo}>
						<div className={getItemClassName(item)} onClick={() => openPhoto(item)}>
							<div className="gallery-item-inner">
								<img src={getSmallPhoto(item)} alt={item.desc} />
								{item.desc && <p className="desc">
									{item.desc}
								</p>}
								{ state.debug && <div className="debug-caption">
									{getDebugCaption(item, ind, page)}
								</div> }
							</div>
						</div>
					</div>)}
				</div>
				<div className="pagger">
					<Link to={getLink(ROUTES.galeries)} className="back">
						{trans('common.gallery.back')}
					</Link>
					{ hasPrev && <>
						<Link to={getPageUrl(page - 1)} className="prev hover">
							{trans('galleryList.prev')}
						</Link>
						<Link to={getPageUrl(FIRST_PAGE)} className="item hover">
							1
						</Link>
						<span className="dots">...</span>
					</>}
					{pages.map(item => <Link to={item.url} key={item.url} className={item.className}>
						{item.ind}
					</Link>)}
					{hasNext && maxPage - page >= PAGGING_MAX_PAGES && <>
						<span className="dots">...</span>
						<Link to={getPageUrl(maxPage)} className="item hover">
							{maxPage}
						</Link>
					</>}
					{ hasNext && <Link to={getPageUrl(page + 1)} className="next hover">
						{trans('galleryList.next')}
					</Link> }
				</div>
			</div>
		</div>
		{ state.photo && <ModalGallery photo={state.photo} source={source[page - 1]} onClose={modalGalleryClose} /> }
	</>;
}
